/**
 * The toast queue — an app-wide store, not component state.
 *
 * `toast(...)` is called from places that are not always inside a render: a mutation's `.then`, a
 * hook that has no JSX of its own, `apps/dashboard/src/support/hooks.ts`'s own success and failure
 * paths. Modelling that as context would mean either threading a dispatcher through every hook that
 * might report an outcome, or reaching for `useContext` outside a component — so this follows the
 * same shape `theme/current.ts` already uses for exactly this reason: one module-level store, a
 * subscribe function, and a `useSyncExternalStore` hook that lets a component read it reactively
 * without holding a second copy that could disagree with it.
 *
 * ## Why dismissal is time-based here and Radix's own timer is not used for it
 *
 * Radix's `Root` accepts a `duration` and closes itself, but "closed" and "gone from the list" are
 * different moments: the exit needs to be observable so `Toaster` can stop rendering the entry rather
 * than leave a closed-but-mounted node behind, and this store is the one place that distinction is
 * made. Auto-dismissal here is a plain `setTimeout` that removes the entry from the queue, which
 * `ToastRoot`'s `onOpenChange={false}` calls into as well when a visitor dismisses it directly — one
 * removal path for both.
 */
import { useSyncExternalStore } from 'react';
const defaultDuration = 5_000;
let entries = [];
const listeners = new Set();
const timers = new Map();
const notify = () => {
    for (const listener of listeners)
        listener();
};
const clearTimer = (id) => {
    const timer = timers.get(id);
    if (timer !== undefined) {
        clearTimeout(timer);
        timers.delete(id);
    }
};
/** Remove one entry. Safe to call more than once for the same id. */
export const dismissToast = (id) => {
    clearTimer(id);
    if (!entries.some((entry) => entry.id === id))
        return;
    entries = entries.filter((entry) => entry.id !== id);
    notify();
};
let nextId = 0;
/** A fresh id per call, independent of wall-clock time so two toasts issued in the same tick differ. */
const generateId = () => {
    nextId += 1;
    return `toast-${String(nextId)}`;
};
/** Enqueue one toast and schedule its automatic dismissal. Returns the id, for an early dismissal. */
export const toast = (input) => {
    const id = generateId();
    const entry = {
        id,
        title: input.title,
        variant: input.variant ?? 'default',
        ...(input.description === undefined ? {} : { description: input.description }),
        ...(input.action === undefined ? {} : { action: input.action }),
    };
    entries = [...entries, entry];
    notify();
    timers.set(id, setTimeout(() => dismissToast(id), input.duration ?? defaultDuration));
    return id;
};
const subscribe = (listener) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
const getSnapshot = () => entries;
/** The live queue, for `Toaster` to render. */
export const useToasts = () => useSyncExternalStore(subscribe, getSnapshot);
/**
 * Test-only reset. Real usage never needs it: the queue empties itself as toasts are dismissed, and a
 * page navigation does not clear it on purpose — a mutation's outcome should survive the reconciling
 * navigation that usually follows it.
 */
export const resetToasts = () => {
    for (const id of timers.keys())
        clearTimer(id);
    entries = [];
    notify();
};
//# sourceMappingURL=store.js.map